import * as React from 'react'
import { ScrollView, View, Text, TouchableOpacity, Platform, RefreshControl } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import IncidentsHomeSection from './OrgaIncidentsHomeSection'
import DraftsButton from '@components/button/DraftsButton'

import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'
import * as FiltreStore from '@store/filtre/filtre'
import { emptyFilters } from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'
import useConnectivity from '@utils/useConnectivity'
import useDrafts from '@utils/useDrafts'

import { canUserCreateIncident } from '@screens/incident/utils'

import { store, refresh } from '@utils/useSiteChangeHandler'

import api from '@api/api'
import Logger from '@utils/logger'

const MAIN_VERTICAL_PADDING = 24

interface Props {
  onBack?: () => void
}

const OrgaSiteScreen = ({ onBack }: Props) => {
  const navigation = useNavigation()
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const draftsCount = useDrafts()[0]

  const [online, loading] = useConnectivity()

  const [stats, setStats] = React.useState<IncidentsStatistiquesResponse>()

  const siteInfo = useReducer(SiteStore.store, (s) => s.site!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const user = useReducer(UserStore.store, (s) => s.user)

  const status = useReducer(store, (s) => s.status!)

  const canCreateIncident = canUserCreateIncident(
    user,
    manifestation && manifestations.inProgress.find((m) => m.id === manifestation.id)
  )

  React.useEffect(() => {
    if (manifestation) {
      api.incident.stats(siteInfo.id, manifestation.id).then(setStats).catch(Logger.error)
    }
  }, [manifestation])

  const displayedIncidents = React.useMemo(
    () => incidents.filter((i) => i.manifestationId === manifestation?.id),
    [manifestation]
  )

  const startButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push(`/incident/start`),
  }

  const renderBottomButton =
    isPhone && canCreateIncident
      ? () => (
          <BottomButton width={width}>
            <Button status="active" label={startButton.label} onClick={startButton.onClick} />
          </BottomButton>
        )
      : undefined

  const refreshOnPull =
    online && !loading ? (
      <RefreshControl refreshing={status === 'loading'} onRefresh={() => refresh(siteInfo.id, scopes)} />
    ) : undefined

  return (
    <Root
      loading={status === 'loading'}
      renderFixedBottom={renderBottomButton}
      header={manifestation?.libelle || i18n.t('screens.site.overview')}
      onLeft={onBack}
      fixed>
      <ScrollView
        scrollIndicatorInsets={{ right: 1 }}
        keyboardShouldPersistTaps="handled"
        scrollEventThrottle={16}
        refreshControl={refreshOnPull}>
        {!isPhone && !!manifestation && (
          <Header
            title={i18n.t(`screens.site.overview`)}
            buttons={canCreateIncident ? [startButton] : []}
            links={[
              { label: i18n.t(`screens.site.sites.${siteInfo.codeLieu}`), onClick: () => undefined },
              { label: manifestation.libelle, onClick: () => undefined },
            ]}
          />
        )}
        <>
          <IncidentIndicators>
            <IndicatorsGroup>
              <IndicatorTitle>{i18n.t('components.orgaIncidentListSection.indicators.prestation')}</IndicatorTitle>
              <IndicatorsRow>
                <Indicator
                  isLeft
                  activeOpacity={0.9}
                  onPress={() => {
                    FiltreStore.actions.setIncidentFilter({ ...emptyFilters, category: ['prestation'] })
                    navigation.push('/incidents')
                  }}>
                  <IndicatorValue>{stats?.enCoursPrestation}</IndicatorValue>
                  <IndicatorLabel>{i18n.t('components.orgaIncidentListSection.indicators.inProgress')}</IndicatorLabel>
                </Indicator>
                <Indicator
                  isLeft={false}
                  activeOpacity={0.9}
                  onPress={() => {
                    FiltreStore.actions.setIncidentFilter({ ...emptyFilters, category: ['prestation'], created: true })
                    navigation.push('/incidents')
                  }}>
                  <IndicatorValue>{stats?.creePrestation}</IndicatorValue>
                  <IndicatorLabel>{i18n.t('components.orgaIncidentListSection.indicators.created')}</IndicatorLabel>
                </Indicator>
              </IndicatorsRow>
            </IndicatorsGroup>
            <IndicatorsGroup>
              <IndicatorTitle>{i18n.t('components.orgaIncidentListSection.indicators.infrastructure')}</IndicatorTitle>
              <IndicatorsRow>
                <Indicator
                  isLeft
                  activeOpacity={0.9}
                  onPress={() => {
                    FiltreStore.actions.setIncidentFilter({ ...emptyFilters, category: ['infrastructure'] })
                    navigation.push('/incidents')
                  }}>
                  <IndicatorValue>{stats?.enCoursInfrastructure}</IndicatorValue>
                  <IndicatorLabel>{i18n.t('components.orgaIncidentListSection.indicators.inProgress')}</IndicatorLabel>
                </Indicator>
                <Indicator
                  isLeft={false}
                  activeOpacity={0.9}
                  onPress={() => {
                    FiltreStore.actions.setIncidentFilter({
                      ...emptyFilters,
                      category: ['infrastructure'],
                      created: true,
                    })
                    navigation.push('/incidents')
                  }}>
                  <IndicatorValue>{stats?.creeInfrastructure}</IndicatorValue>
                  <IndicatorLabel>{i18n.t('components.orgaIncidentListSection.indicators.created')}</IndicatorLabel>
                </Indicator>
              </IndicatorsRow>
            </IndicatorsGroup>
            <IndicatorsGroup>
              <IndicatorsRow>
                <SingleIndicator>
                  <IndicatorTitle>
                    {i18n.t('components.orgaIncidentListSection.indicators.intervention')}
                  </IndicatorTitle>

                  <Indicator isLeft disabled>
                    <IndicatorValue>
                      {stats?.delaiInterventionMinute || stats?.delaiInterventionMinute === 0
                        ? `${Math.floor(stats.delaiInterventionMinute / 60)}h${stats.delaiInterventionMinute % 60}`
                        : i18n.t('errors.NC')}
                    </IndicatorValue>
                    <IndicatorLabel>
                      {i18n.t('components.orgaIncidentListSection.indicators.interventionDuration')}
                    </IndicatorLabel>
                  </Indicator>
                </SingleIndicator>
                <SingleIndicator isLeft>
                  <IndicatorTitle>{i18n.t('components.orgaIncidentListSection.indicators.total')}</IndicatorTitle>
                  <Indicator
                    isLeft={false}
                    onPress={() => {
                      FiltreStore.actions.setIncidentFilter(emptyFilters)
                      navigation.push('/incidents')
                    }}>
                    <IndicatorValue>{stats?.totalDeclaration}</IndicatorValue>
                    <IndicatorLabel>{i18n.t('components.orgaIncidentListSection.indicators.incidents')}</IndicatorLabel>
                  </Indicator>
                </SingleIndicator>
              </IndicatorsRow>
            </IndicatorsGroup>
          </IncidentIndicators>

          <MainContainer>
            {draftsCount === 0 && (
              <AlertButtonContainer>
                <DraftsButton count={draftsCount} onClick={() => navigation.push('/drafts')} />
              </AlertButtonContainer>
            )}

            <IncidentsHomeSection incidents={displayedIncidents} onSeeAll={() => navigation.push('/incidents')} />
          </MainContainer>
        </>
      </ScrollView>
    </Root>
  )
}

export default OrgaSiteScreen

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`
const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: 16px;
`

const IncidentIndicators = styled(View)`
  margin-bottom: 16px;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 24px;` : `flex-wrap: wrap;`)}
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '4px' : '8px 12px 32px')};
  background-color: ${(props) => props.theme.colors.incident};
`
const IndicatorsGroup = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
  min-width: 250px;
  margin: 24px 12px 0;
`
const IndicatorTitle = styled(Text)`
  margin-bottom: 8px;
  ${(props) => props.theme.fonts.Subheadline};
`
const IndicatorsRow = styled(View)`
  flex-direction: row;
`
const Indicator = styled(TouchableOpacity)<{ isLeft?: boolean }>`
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
  min-height: 80px;
  flex: 1;
  ${(props) => props.isLeft !== undefined && `margin: ${props.isLeft ? '0 1px 0 0' : '0 0 0 1px'};`}
`
const IndicatorValue = styled(Text)<{ isSmall?: boolean; inRow?: boolean }>`
  ${(props) => props.theme.fonts.Title3};
  font-size: ${(props) => (props.isSmall ? 15 : 24)}px;
  ${(props) => props.theme.breakpoint === 'phone' && `margin: 0 15px;`}
  ${(props) => props.inRow && `margin-right: 8px;`}
`
const IndicatorLabel = styled(Text)<{ isSmall?: boolean; inRow?: boolean }>`
  ${(props) => props.theme.breakpoint === 'phone' && props.isSmall && `flex: 1;`}
  ${(props) => props.theme.fonts.Footnote};
  font-size: ${(props) => (props.theme.breakpoint === 'phone' && props.isSmall ? 11 : 14)}px;
  ${Platform.OS === 'web' && `line-height: 15px;`}
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: ${(props) => (props.theme.breakpoint === 'phone' || props.inRow ? 'left' : 'center')};
  ${Platform.OS === 'web' && 'word-break: break-word;'}
`
const SingleIndicator = styled(View)<{ isLeft?: boolean }>`
  flex: 1;
  ${(props) => props.isLeft && `margin-left: 24px;`}
`

const AlertButtonContainer = styled(View)`
  margin-bottom: 32px;
`
